// src/features/rrhh/horarios/components/HorariosTable.jsx
import React from "react";
import { Avatar, Box, CircularProgress, Stack, Tooltip, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";

function toMin(hm) {
  if (!hm) return null;
  const [h, m] = String(hm).slice(0, 5).split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

function dayOfWeekFromDate(workDate, weekStart) {
  if (!workDate || !weekStart) return null;
  const wd = new Date(workDate + "T00:00:00");
  const ws = new Date(weekStart + "T00:00:00");
  const diff = Math.round((wd - ws) / 86400000);
  if (diff < 0 || diff > 6) return null;
  return diff + 1;
}

function fmtHour(time) {
  if (!time) return "";
  return String(time).slice(0, 5).replace(/^0/, "");
}

function shiftStartOrder(shift) {
  return Number(shift?.startDayOffset ?? shift?.start_day_offset ?? 0) * 24 * 60 +
    (toMin(shift?.startTime || shift?.start_time) ?? 0);
}

function formatMinutesAsHours(value) {
  const minutes = Math.max(0, Math.round(Number(value) || 0));
  const hoursPart = Math.floor(minutes / 60);
  const minutesPart = String(minutes % 60).padStart(2, "0");

  return `${hoursPart}:${minutesPart} hs`;
}

const WEEK_DAYS_SHORT = ["Lun", "Mar", "Mie", "Jue", "Vie", "Sab", "Dom"];
const WEEK_KEYS = [1, 2, 3, 4, 5, 6, 7];

const AREA_COLORS = [
  {
    main: "#34C759",
    soft: "rgba(52,199,89,0.22)",
    border: "rgba(52,199,89,0.55)",
  },
  {
    main: "#16B8C7",
    soft: "rgba(22,184,199,0.22)",
    border: "rgba(22,184,199,0.55)",
  },
  {
    main: "#C13CE3",
    soft: "rgba(193,60,227,0.22)",
    border: "rgba(193,60,227,0.55)",
  },
  {
    main: "#F59E0B",
    soft: "rgba(245,158,11,0.22)",
    border: "rgba(245,158,11,0.55)",
  },
  {
    main: "#6366F1",
    soft: "rgba(99,102,241,0.22)",
    border: "rgba(99,102,241,0.55)",
  },
  {
    main: "#EC4899",
    soft: "rgba(236,72,153,0.20)",
    border: "rgba(236,72,153,0.52)",
  },
  {
    main: "#10B981",
    soft: "rgba(16,185,129,0.20)",
    border: "rgba(16,185,129,0.52)",
  },
  {
    main: "#EF4444",
    soft: "rgba(239,68,68,0.18)",
    border: "rgba(239,68,68,0.50)",
  },
  {
    main: "#8B5CF6",
    soft: "rgba(139,92,246,0.20)",
    border: "rgba(139,92,246,0.52)",
  },
  {
    main: "#06B6D4",
    soft: "rgba(6,182,212,0.20)",
    border: "rgba(6,182,212,0.52)",
  },
];

function getAreaColor(areaId) {
  const id = Number(areaId || 0);
  if (!id) return AREA_COLORS[0];

  return AREA_COLORS[Math.abs(id) % AREA_COLORS.length];
}

function buildWeekHeaders(cursorRange) {
  if (!cursorRange?.desde) {
    return WEEK_DAYS_SHORT.map((d, i) => ({ key: i + 1, label: d }));
  }

  const [y, m, d] = cursorRange.desde.split("-").map(Number);
  const start = new Date(y, m - 1, d);

  return WEEK_DAYS_SHORT.map((dayName, i) => {
    const dt = new Date(start);
    dt.setDate(dt.getDate() + i);
    return { key: i + 1, label: `${dayName} ${dt.getDate()}` };
  });
}

function workDateFromDay(dayKey, weekStart) {
  if (!weekStart) return "";

  const [y, m, d] = weekStart.split("-").map(Number);
  const base = new Date(y, m - 1, d);
  base.setDate(base.getDate() + (dayKey - 1));

  const pad = (n) => String(n).padStart(2, "0");

  return `${base.getFullYear()}-${pad(base.getMonth() + 1)}-${pad(
    base.getDate()
  )}`;
}

function normalizeId(value) {
  return value === null || value === undefined || value === "" ? "" : String(value);
}

function getSectorLabel(shift) {
  const text =
    shift?.sectorName ??
    shift?.sector_name ??
    shift?.sector?.name ??
    shift?.sectionName ??
    shift?.section_name ??
    shift?.section ??
    "";

  return String(text || "").trim();
}

function truncateSectorLabel(value, maxLength = 12) {
  const text = String(value || "").trim();
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
}

function buildAreaFallbackFromShifts(emp, areasById) {
  const map = new Map();

  for (const shift of emp?.shifts || []) {
    const areaId = normalizeId(shift.areaId || shift.area_id);
    if (!areaId) continue;

    const areaFromList = areasById.get(areaId);

    map.set(areaId, {
      id: areaId,
      name: areaFromList?.name || `Área ${areaId}`,
    });
  }

  return [...map.values()];
}

function buildAreaSections({ employees, areas, selectedAreaId }) {
  const areasById = new Map((areas || []).map((area) => [String(area.id), area]));

  const sectionMap = new Map();

  const ensureSection = (area) => {
    const areaId = normalizeId(area?.id) || "sin-area";
    const areaName =
      area?.name ||
      area?.nombre ||
      (areaId === "sin-area" ? "Sin área" : `Área ${areaId}`);

    if (!sectionMap.has(areaId)) {
      sectionMap.set(areaId, {
        areaId,
        areaName,
        color: getAreaColor(areaId),
        rows: [],
      });
    }

    return sectionMap.get(areaId);
  };

  for (const emp of employees || []) {
    let employeeAreas = Array.isArray(emp.areas) ? emp.areas : [];

    if (selectedAreaId) {
      const selected = String(selectedAreaId);
      const areaFromList = areasById.get(selected);

      employeeAreas = [
        {
          id: selected,
          name:
            areaFromList?.name ||
            employeeAreas.find((area) => String(area.id) === selected)?.name ||
            `Área ${selected}`,
        },
      ];
    } else {
      if (!employeeAreas.length) {
        employeeAreas = buildAreaFallbackFromShifts(emp, areasById);
      }

      if (!employeeAreas.length) {
        employeeAreas = [{ id: "sin-area", name: "Sin área" }];
      }
    }

    for (const area of employeeAreas) {
      const section = ensureSection(area);

      section.rows.push({
        ...emp,
        groupAreaId: normalizeId(area.id),
        groupAreaName: area.name || area.nombre || "Sin área",
      });
    }
  }

  return [...sectionMap.values()]
    .map((section) => {
      const sortedRows = section.rows.sort((a, b) =>
        String(a.fullName || "").localeCompare(String(b.fullName || ""), "es")
      );

      const totalMinutes = sortedRows.reduce((sum, row) => {
        const areaMinutes = Number(row.minutesByArea?.[section.areaId] || 0);
        return sum + areaMinutes;
      }, 0);

      return {
        ...section,
        rows: sortedRows,
        totalMinutes,
      };
    })
    .filter((section) => section.rows.length > 0);
}

export default React.memo(function HorariosTable({
  loading,
  fetchError = "",
  cursorRange,
  employees = [],
  areas = [],
  selectedAreaId = "",
  selectedSectionId = "",
  selectedBranchId = "",
  onCellClick,
}) {
  const weekHeaders = buildWeekHeaders(cursorRange);
  const weekStart = cursorRange?.desde;

  const sections = buildAreaSections({
    employees,
    areas,
    selectedAreaId,
  });

  const empDayMap = new Map();

  (employees || []).forEach((emp) => {
    const empId = normalizeId(emp.employeeId);
    const dayMap = new Map();

    (emp.shifts || []).forEach((sh) => {
      const day = dayOfWeekFromDate(sh.workDate, weekStart);
      if (day == null) return;

      if (!dayMap.has(day)) dayMap.set(day, []);
      dayMap.get(day).push(sh);
    });

    empDayMap.set(empId, dayMap);
  });

  return (
    <Stack
      spacing={2}
      sx={{
        width: "100%",
        p: 1,
        bgcolor: "neutral.150",
        borderRadius: "20px",
        flex: 1,
        minHeight: 0,
      }}
    >
      <Box sx={{ display: "flex", gap: 1, width: "100%", pr: 2 }}>
        <Box sx={{ width: "18%" }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            sx={{
              width: "100%",
              height: "6.4vh",
              px: 2.5,
              py: 0.5,
              bgcolor: "neutral.600",
              borderRadius: "34px",
              boxShadow: "inset 0px 0px 8px rgba(75, 75, 88, 1)",
            }}
          >
            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "logoblanco" }}>
              Empleados
            </Typography>
          </Stack>
        </Box>

        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              px: 1,
              bgcolor: "neutral.600",
              borderRadius: "15px",
              boxShadow: "inset 0px 0px 8px rgba(75, 75, 88, 1)",
              height: "6.4vh",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr) 95px",
                gap: 1,
                width: "100%",
              }}
            >
              {weekHeaders.map((h) => (
                <Typography
                  key={h.key}
                  sx={{
                    textAlign: "center",
                    fontSize: "15px",
                    fontWeight: 500,
                    color: "logoblanco",
                  }}
                >
                  {h.label}
                </Typography>
              ))}
              <Typography
                sx={{
                  textAlign: "center",
                  fontSize: "15px",
                  fontWeight: 500,
                  color: "logoblanco",
                }}
              >
                Total
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          width: "100%",
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          scrollbarGutter: "stable",
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(17,24,39,.5) transparent",
          "&::-webkit-scrollbar": { width: "10px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(17,24,39,.25)",
            borderRadius: "999px",
            border: "3px solid transparent",
            backgroundClip: "content-box",
          },
        }}
      >
        <Stack spacing={1} sx={{ width: "100%", pr: 1 }}>
          {!loading &&
            !fetchError &&
            sections.map((section) => {
              const areaColor = section.color;

              return (
                <Box
                  key={section.areaId}
                  sx={{
                    width: "100%",
                    borderRadius: "18px",
                    bgcolor: areaColor.soft,
                    border: `1px solid ${areaColor.border}`,
                    p: 0.75,
                    boxShadow: "inset 0px 0px 3px rgba(0,0,0,0.04)",
                    overflow: "hidden",
                  }}
                >
                  <Box sx={{ display: "flex", gap: 1, width: "100%", mb: 0.75 }}>
                    <Box sx={{ width: "18%" }}>
                      <Box
                        sx={{
                          height: 28,
                          borderRadius: "8px",
                          px: 1.4,
                          display: "flex",
                          alignItems: "center",
                          bgcolor: areaColor.main,
                          boxShadow: "inset 0px 0px 3px rgba(0,0,0,0.12)",
                        }}
                      >
                        <Typography
                          sx={{
                            color: "#0F172A",
                            fontSize: 15,
                            fontWeight: 900,
                            lineHeight: 1,
                          }}
                          noWrap
                        >
                          {section.areaName}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "repeat(7, 1fr) 95px",
                          gap: 1,
                        }}
                      >
                        <Box sx={{ gridColumn: "1 / 8" }} />
                        <Box
                          sx={{
                            height: 28,
                            borderRadius: "8px",
                            bgcolor: areaColor.main,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxShadow: "inset 0px 0px 3px rgba(0,0,0,0.12)",
                          }}
                        >
                          <Typography
                            sx={{
                              color: "#0F172A",
                              fontSize: 12,
                              fontWeight: 900,
                              lineHeight: 1,
                            }}
                          >
                            {formatMinutesAsHours(section.totalMinutes)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  <Stack spacing={0.55}>
                    {section.rows.map((emp) => {
                      const empId = normalizeId(emp.employeeId);
                      const dayMap = empDayMap.get(empId) || new Map();

                      return (
                        <Box
                          key={`${section.areaId}-${empId}`}
                          sx={{
                            display: "flex",
                            gap: 1,
                            width: "100%",
                            alignItems: "stretch",
                            bgcolor: "transparent",
                            borderRadius: "14px",
                            p: 0.2,
                          }}
                        >
                          <Box sx={{ width: "18%" }}>
                            <Box
                              sx={{
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                px: 0.5,
                                bgcolor: "neutral.200",
                                borderRadius: "52px",
                                boxShadow: "inset 0px 0px 4px rgba(165, 165, 178, 1)",
                              }}
                            >
                              <Avatar
                                sx={{
                                  width: 32,
                                  height: 32,
                                  bgcolor: "secondary.300",
                                }}
                              />

                              <Typography
                                sx={{
                                  flex: 1,
                                  fontSize: "14px",
                                  fontWeight: 600,
                                  color: "primary.900",
                                  textAlign: "start",
                                }}
                                noWrap
                              >
                                {emp.fullName || `Empleado ${empId}`}
                              </Typography>
                            </Box>
                          </Box>

                          <Box sx={{ flex: 1 }}>
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "repeat(7, 1fr) 95px",
                                gap: 1,
                              }}
                            >
                              {WEEK_KEYS.map((day) => {
                                const allDayItems = (dayMap.get(day) || [])
                                  .slice()
                                  .sort((a, b) => shiftStartOrder(a) - shiftStartOrder(b));

                                const items = allDayItems.filter(
                                  (shift) =>
                                    normalizeId(shift.areaId || shift.area_id) ===
                                    normalizeId(section.areaId)
                                );

                                const cellDate = workDateFromDay(day, weekStart);

                                if (items.length === 0) {
                                  return (
                                    <Box
                                      key={`${section.areaId}-${empId}-${day}`}
                                      sx={{
                                        minHeight: "6.5vh",
                                        borderRadius: "14px",
                                        bgcolor: "rgba(255,255,255,0.18)",
                                        border: "2px solid",
                                        borderColor: areaColor.border,
                                        cursor: "pointer",
                                        transition:
                                          "background-color 0.15s, transform 0.15s",
                                        "&:hover": {
                                          bgcolor: "rgba(255,255,255,0.30)",
                                          transform: "translateY(-1px)",
                                        },
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                      onClick={() =>
                                        onCellClick?.({
                                          employeeId: empId,
                                          fullName: emp.fullName,
                                          workDate: cellDate,
                                          shift: null,
                                          allDayShifts: allDayItems,
                                          suggestedAreaId:
                                            section.areaId === "sin-area"
                                              ? null
                                              : section.areaId,
                                          suggestedSectionId: selectedSectionId || null,
                                          suggestedBranchId: selectedBranchId || null,
                                        })
                                      }
                                    />
                                  );
                                }

                                const multiShift = items.length > 1;
                                const denseShift = items.length >= 3;
                                const showsSectorRows =
                                  !denseShift &&
                                  items.some((shift) => Boolean(getSectorLabel(shift)));
                                const sectorRowHeight = 40;

                                return (
                                  <Box
                                    key={`${section.areaId}-${empId}-${day}`}
                                    sx={{
                                      minHeight: showsSectorRows
                                        ? `${items.length * sectorRowHeight + Math.max(0, items.length - 1) * 3}px`
                                        : "6.5vh",
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: "3px",
                                    }}
                                  >
                                    {items.map((sh, idx) => {
                                      const hIni = fmtHour(sh.startTime);
                                      const hFin = fmtHour(sh.endTime);

                                      const hourLabel =
                                        hIni && hFin
                                          ? `${hIni} - ${hFin}hs`
                                          : "";
                                      const sectorLabel = getSectorLabel(sh);
                                      const sectorDisplay = truncateSectorLabel(sectorLabel);

                                      return (
                                        <Tooltip
                                          key={sh.id || idx}
                                          title={sectorLabel ? `Sector: ${sectorLabel}` : ""}
                                          arrow
                                          disableHoverListener={!sectorLabel}
                                        >
                                        <Box
                                          sx={{
                                            flex: showsSectorRows ? `0 0 ${sectorRowHeight}px` : 1,
                                            minHeight: showsSectorRows ? `${sectorRowHeight}px` : 0,
                                            borderRadius: multiShift ? "10px" : "14px",
                                            bgcolor: "#4caf50",
                                            border: "2px solid #388e3c",
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            px: 0.7,
                                            py: 0.3,
                                            overflow: "hidden",
                                            cursor: "pointer",
                                            transition: "filter 0.15s",
                                            "&:hover": { filter: "brightness(1.1)" },
                                          }}
                                          onClick={() =>
                                            onCellClick?.({
                                              employeeId: empId,
                                              fullName: emp.fullName,
                                              workDate: sh.workDate,
                                              shift: sh,
                                              allDayShifts: allDayItems,
                                              suggestedAreaId: section.areaId,
                                              suggestedSectionId: selectedSectionId || null,
                                              suggestedBranchId: selectedBranchId || null,
                                            })
                                          }
                                        >
                                          {hourLabel && (
                                            <Stack
                                              direction="row"
                                              alignItems="center"
                                              justifyContent="center"
                                              spacing={0.3}
                                              sx={{
                                                width: "100%",
                                                overflow: "hidden",
                                              }}
                                            >
                                              <Typography
                                                noWrap
                                                sx={{
                                                  minWidth: 0,
                                                  fontSize: multiShift ? "9px" : "11px",
                                                  fontWeight: 700,
                                                  color: "#fff",
                                                  lineHeight: 1.15,
                                                  textAlign: "center",
                                                  overflow: "hidden",
                                                  textOverflow: "ellipsis",
                                                }}
                                              >
                                                {hourLabel}
                                              </Typography>

                                              {denseShift && sectorLabel ? (
                                                <LabelOutlinedIcon
                                                  sx={{
                                                    flexShrink: 0,
                                                    fontSize: 11,
                                                    color: "#e0f2fe",
                                                    filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.18))",
                                                  }}
                                                />
                                              ) : null}
                                            </Stack>
                                          )}
                                          {!denseShift && sectorLabel ? (
                                            <Typography
                                              noWrap
                                              sx={{
                                                maxWidth: "100%",
                                                mt: 0.25,
                                                px: 0.65,
                                                py: 0.12,
                                                borderRadius: "8px",
                                                bgcolor: "#e0f2fe",
                                                border: "1px solid rgba(14, 116, 144, 0.28)",
                                                fontSize: "9px",
                                                fontWeight: 800,
                                                lineHeight: 1.15,
                                                color: "#075985",
                                                textAlign: "center",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                              }}
                                            >
                                              {sectorDisplay}
                                            </Typography>
                                          ) : null}
                                        </Box>
                                        </Tooltip>
                                      );
                                    })}
                                  </Box>
                                );
                              })}
                              
                              <Box
                                sx={{
                                  minHeight: "6.5vh",
                                  borderRadius: "14px",
                                  bgcolor: "secondary.300",
                                  border: "2px solid",
                                  borderColor: "secondary.500",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexDirection: "column",
                                  px: 1,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "12px",
                                    fontWeight: 700,
                                    color: "#0F172A",
                                  }}
                                >
                                  {formatMinutesAsHours(emp.minutesByArea?.[section.areaId])}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              );
            })}
        </Stack>

        {loading && (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
            <CircularProgress size={36} sx={{ color: "secondary.500" }} />
            <Typography sx={{ fontSize: 13, color: "primary.900", opacity: 0.6, mt: 1.5 }}>
              Cargando turnos…
            </Typography>
          </Stack>
        )}

        {!loading && fetchError && (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
            <ErrorOutlineIcon sx={{ fontSize: 40, color: "error.main", mb: 1 }} />
            <Typography
              sx={{
                fontSize: 14,
                color: "error.main",
                fontWeight: 600,
                textAlign: "center",
              }}
            >
              {fetchError}
            </Typography>
          </Stack>
        )}

        {!loading && !fetchError && sections.length === 0 && (
          <Typography
            sx={{
              fontSize: 14,
              color: "primary.900",
              opacity: 0.7,
              textAlign: "center",
              py: 2,
            }}
          >
            Sin empleados para mostrar
          </Typography>
        )}
      </Box>
    </Stack>
  );
});
